import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { filter, map, Observable } from "rxjs";

import { YmkSdkShadeFinderConfiguration } from "@hermes/api-model-shade-finder";

import * as shadeFinderActions from "../actions/shade-finder.actions";
import { SHADE_FINDER_CONFIGURATION_UNDEFINED } from "../constants/shade-finder.constant";
import { ShadeFinderModalConfiguration } from "../model/shade-finder.model";
import { ShadeFinderState } from "../reducers/shade-finder-state";
import {
    selectCameraDimensionState,
    selectIsApprovalState,
    selectModalStatusState,
    selectSdkErrorState,
    selectShadeFinderConfiguration,
    shadeFinderModalConfiguration,
} from "../selectors/shade-finder.selectors";
import {
    ShadeFinderCameraDimension,
    ShadeFinderModalStatus,
    ShadeFinderSdkError,
} from "../types/shade-finder.types";

@Injectable()
export class ShadeFinderFacade {
    // Selectors
    public shadeFinderConfiguration$: Observable<YmkSdkShadeFinderConfiguration> =
        this.store.select(selectShadeFinderConfiguration).pipe(
            filter(
                (shadeFinderConfiguration) =>
                    shadeFinderConfiguration !==
                    SHADE_FINDER_CONFIGURATION_UNDEFINED,
            ),
            map(
                (shadeFinderConfiguration) =>
                    shadeFinderConfiguration as YmkSdkShadeFinderConfiguration,
            ),
        );

    public modalStatus$: Observable<ShadeFinderModalStatus> = this.store.select(
        selectModalStatusState,
    );

    public cameraDimension$: Observable<ShadeFinderCameraDimension> =
        this.store.select(selectCameraDimensionState);

    public isApproval$: Observable<boolean> = this.store.select(
        selectIsApprovalState,
    );

    public sdkError$: Observable<ShadeFinderSdkError> =
        this.store.select(selectSdkErrorState);

    public shadeFinderModalConfiguration$: Observable<ShadeFinderModalConfiguration> =
        this.store.select(shadeFinderModalConfiguration);

    constructor(private store: Store<ShadeFinderState>) {}

    // Actions
    public fetchShadeFinderConfiguration(): void {
        this.store.dispatch(shadeFinderActions.fetchShadeFinderConfiguration());
    }

    public changeScreenOrientation(): void {
        this.store.dispatch(shadeFinderActions.changeScreenOrientation());
    }

    public userApprove(): void {
        this.store.dispatch(shadeFinderActions.userApprove());
    }

    public userDisapprove(): void {
        this.store.dispatch(shadeFinderActions.userDisapprove());
    }

    public launchCamera(): void {
        this.store.dispatch(shadeFinderActions.launchCamera());
    }

    public closeModal(): void {
        this.store.dispatch(shadeFinderActions.closeModal());
    }

    public openModal(): void {
        this.store.dispatch(shadeFinderActions.openModal());
    }

    public destroyModal(): void {
        this.store.dispatch(shadeFinderActions.destroyModal());
    }

    public onAir(): void {
        this.store.dispatch(shadeFinderActions.onAir());
    }

    public catchSdkError(sdkError: ShadeFinderSdkError): void {
        this.store.dispatch(shadeFinderActions.catchSdkError({ sdkError }));
    }

    public updateCameraDimension(
        cameraDimension: ShadeFinderCameraDimension,
    ): void {
        this.store.dispatch(
            shadeFinderActions.updateCameraDimension({ cameraDimension }),
        );
    }

    public shadeFinderStarted(): void {
        this.store.dispatch(shadeFinderActions.shadeFinderStarted());
    }

    public shadeFinderComplete(): void {
        this.store.dispatch(shadeFinderActions.shadeFinderComplete());
    }
}
